import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, GridLegacy, IconButton, InputLabel, MenuItem,
  Paper, Rating, Select, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
  Accordion, AccordionSummary, AccordionDetails, Alert, Tooltip,
  ToggleButton, ToggleButtonGroup, Autocomplete, TablePagination,
} from '@mui/material';
import {
  Add, Delete, Edit, ExpandMore, Visibility, ArrowBack,
  Person, Work, TrendingUp, TrendingDown, TrendingFlat,
} from '@mui/icons-material';
import { interviewFeedbackService, userService } from '../services/api';
import { usePagination } from '../hooks/usePagination';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SkillRating {
  skill: string;
  rating: number;
  comments: string;
}

interface ScenarioRating {
  question: string;
  response: string;
  rating: number;
}

interface FeedbackForm {
  interviewType: 'External' | 'Internal';
  candidateName: string;
  position: string;
  interviewDate: string;
  interviewerName: string;
  experience: string;
  currentCompany: string;
  noticePeriod: string;
  currentCTC: string;
  expectedCTC: string;
  employeeId: number | null;
  technicalRatings: SkillRating[];
  frontendRatings: SkillRating[];
  scenarioRatings: ScenarioRating[];
  softSkillsRatings: SkillRating[];
  codingRatings: SkillRating[];
  overallRatings: SkillRating[];
  strengths: string[];
  areasOfImprovement: string[];
  finalDecision: string;
  recommendedLevel: string;
  interviewerRemarks: string;
}

// ─── Default Data ─────────────────────────────────────────────────────────────

const defaultTechnicalRatings: SkillRating[] = [
  'C# / .NET Core', 'ASP.NET Web API', 'Entity Framework', 'SQL Server',
  'LINQ', 'Design Patterns', 'SOLID Principles', 'Microservices',
  'REST API Design', 'Authentication (JWT/OAuth)', 'Azure / AWS',
  'Git & Version Control', 'Unit Testing', 'Performance Optimization',
  'Problem Solving', 'System Design',
].map(s => ({ skill: s, rating: 0, comments: '' }));

const defaultFrontendRatings: SkillRating[] = [
  'JavaScript', 'TypeScript', 'React.js', 'Angular',
  'HTML/CSS', 'State Management', 'API Integration', 'Responsive Design',
].map(s => ({ skill: s, rating: 0, comments: '' }));

const defaultScenarioRatings: ScenarioRating[] = [
  'API performance issue troubleshooting',
  'Database optimization scenario',
  'Production bug investigation',
  'Authentication implementation',
  'Scalability design approach',
].map(q => ({ question: q, response: '', rating: 0 }));

const defaultSoftSkillsRatings: SkillRating[] = [
  'Communication', 'Problem Understanding', 'Team Collaboration',
  'Leadership', 'Client Communication', 'Learning Attitude',
].map(s => ({ skill: s, rating: 0, comments: '' }));

const defaultCodingRatings: SkillRating[] = [
  'Code Quality', 'Naming Conventions', 'Error Handling',
  'Time Complexity Understanding', 'Debugging Skills', 'Solution Completeness',
].map(s => ({ skill: s, rating: 0, comments: '' }));

const defaultOverallRatings: SkillRating[] = [
  'Technical Skills', 'Problem Solving', 'Communication',
  'Cultural Fit', 'Overall Recommendation',
].map(s => ({ skill: s, rating: 0, comments: '' }));

const blankForm = (): FeedbackForm => ({
  interviewType: 'External',
  candidateName: '',
  position: '',
  interviewDate: new Date().toISOString().split('T')[0],
  interviewerName: '',
  experience: '',
  currentCompany: '',
  noticePeriod: '',
  currentCTC: '',
  expectedCTC: '',
  employeeId: null,
  technicalRatings: defaultTechnicalRatings.map(r => ({ ...r })),
  frontendRatings: defaultFrontendRatings.map(r => ({ ...r })),
  scenarioRatings: defaultScenarioRatings.map(r => ({ ...r })),
  softSkillsRatings: defaultSoftSkillsRatings.map(r => ({ ...r })),
  codingRatings: defaultCodingRatings.map(r => ({ ...r })),
  overallRatings: defaultOverallRatings.map(r => ({ ...r })),
  strengths: ['', '', ''],
  areasOfImprovement: ['', '', ''],
  finalDecision: '',
  recommendedLevel: '',
  interviewerRemarks: '',
});

const dtoToForm = (dto: any): FeedbackForm => ({
  interviewType: dto.interviewType || 'External',
  candidateName: dto.candidateName || '',
  position: dto.position || '',
  interviewDate: dto.interviewDate ? dto.interviewDate.split('T')[0] : new Date().toISOString().split('T')[0],
  interviewerName: dto.interviewerName || '',
  experience: dto.experience || '',
  currentCompany: dto.currentCompany || '',
  noticePeriod: dto.noticePeriod || '',
  currentCTC: dto.currentCTC || '',
  expectedCTC: dto.expectedCTC || '',
  employeeId: dto.employeeId || null,
  technicalRatings: dto.technicalRatings?.length ? dto.technicalRatings : defaultTechnicalRatings.map(r => ({ ...r })),
  frontendRatings: dto.frontendRatings?.length ? dto.frontendRatings : defaultFrontendRatings.map(r => ({ ...r })),
  scenarioRatings: dto.scenarioRatings?.length ? dto.scenarioRatings : defaultScenarioRatings.map(r => ({ ...r })),
  softSkillsRatings: dto.softSkillsRatings?.length ? dto.softSkillsRatings : defaultSoftSkillsRatings.map(r => ({ ...r })),
  codingRatings: dto.codingRatings?.length ? dto.codingRatings : defaultCodingRatings.map(r => ({ ...r })),
  overallRatings: dto.overallRatings?.length ? dto.overallRatings : defaultOverallRatings.map(r => ({ ...r })),
  strengths: dto.strengths?.length ? dto.strengths : ['', '', ''],
  areasOfImprovement: dto.areasOfImprovement?.length ? dto.areasOfImprovement : ['', '', ''],
  finalDecision: dto.finalDecision || '',
  recommendedLevel: dto.recommendedLevel || '',
  interviewerRemarks: dto.interviewerRemarks || '',
});

// ─── Reusable sub-components ──────────────────────────────────────────────────

const decisionColor = (d: string) => {
  if (d === 'StrongHire') return 'success';
  if (d === 'Hire') return 'primary';
  if (d === 'Hold') return 'warning';
  if (d === 'Reject') return 'error';
  return 'default';
};

const avgRating = (ratings: SkillRating[]) => {
  const filled = ratings.filter(r => r.rating > 0);
  if (!filled.length) return 0;
  return filled.reduce((s, r) => s + r.rating, 0) / filled.length;
};

const SkillRatingRow: React.FC<{
  item: SkillRating;
  readOnly?: boolean;
  onChange?: (updated: SkillRating) => void;
}> = ({ item, readOnly, onChange }) => (
  <TableRow>
    <TableCell sx={{ fontWeight: 500 }}>{item.skill}</TableCell>
    <TableCell>
      <Rating
        value={item.rating}
        readOnly={readOnly}
        onChange={(_, v) => onChange?.({ ...item, rating: v ?? 0 })}
        size="small"
      />
    </TableCell>
    <TableCell>
      {readOnly ? item.comments : (
        <TextField
          size="small"
          fullWidth
          placeholder="Comments…"
          value={item.comments}
          onChange={e => onChange?.({ ...item, comments: e.target.value })}
        />
      )}
    </TableCell>
  </TableRow>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const InterviewFeedback: React.FC = () => {
  const [view, setView] = useState<'list' | 'form' | 'detail'>('list');
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [detailData, setDetailData] = useState<any | null>(null);
  const [form, setForm] = useState<FeedbackForm>(blankForm());
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDecision, setFilterDecision] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const loadFeedbacks = useCallback(async () => {
    try {
      const data = await interviewFeedbackService.getAll({
        type: filterType || undefined,
        search: search || undefined,
        decision: filterDecision || undefined,
      });
      setFeedbacks(data);
    } catch {
      setMessage('Failed to load feedbacks.');
    }
  }, [filterType, search, filterDecision]);

  useEffect(() => { loadFeedbacks(); }, [loadFeedbacks]);

  useEffect(() => {
    userService.getUsers().then(data => setEmployees(data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm());
    setView('form');
  };

  const openEdit = async (id: number) => {
    try {
      const data = await interviewFeedbackService.getById(id);
      setEditingId(id);
      setForm(dtoToForm(data));
      setView('form');
    } catch {
      setMessage('Failed to load feedback.');
    }
  };

  const openDetail = async (id: number) => {
    try {
      const data = await interviewFeedbackService.getById(id);
      setDetailData(data);
      setView('detail');
    } catch {
      setMessage('Failed to load feedback.');
    }
  };

  const handleSave = async () => {
    if (!form.candidateName.trim() || !form.position.trim()) {
      setMessage('Candidate name and position are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        strengths: form.strengths.filter(s => s.trim()),
        areasOfImprovement: form.areasOfImprovement.filter(s => s.trim()),
      };
      if (editingId) {
        await interviewFeedbackService.update(editingId, payload);
      } else {
        await interviewFeedbackService.create(payload);
      }
      setMessage(editingId ? 'Updated successfully.' : 'Saved successfully.');
      setView('list');
      loadFeedbacks();
    } catch {
      setMessage('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await interviewFeedbackService.delete(id);
      setDeleteConfirm(null);
      loadFeedbacks();
    } catch {
      setMessage('Delete failed.');
    }
  };

  // ── helpers to update nested arrays ──

  const updateTechnical = (i: number, updated: SkillRating) =>
    setForm(f => ({ ...f, technicalRatings: f.technicalRatings.map((r, idx) => idx === i ? updated : r) }));

  const updateFrontend = (i: number, updated: SkillRating) =>
    setForm(f => ({ ...f, frontendRatings: f.frontendRatings.map((r, idx) => idx === i ? updated : r) }));

  const updateScenario = (i: number, updated: ScenarioRating) =>
    setForm(f => ({ ...f, scenarioRatings: f.scenarioRatings.map((r, idx) => idx === i ? updated : r) }));

  const updateSoftSkills = (i: number, updated: SkillRating) =>
    setForm(f => ({ ...f, softSkillsRatings: f.softSkillsRatings.map((r, idx) => idx === i ? updated : r) }));

  const updateCoding = (i: number, updated: SkillRating) =>
    setForm(f => ({ ...f, codingRatings: f.codingRatings.map((r, idx) => idx === i ? updated : r) }));

  const updateOverall = (i: number, updated: SkillRating) =>
    setForm(f => ({ ...f, overallRatings: f.overallRatings.map((r, idx) => idx === i ? updated : r) }));

  // Called unconditionally (rules of hooks) even though the view/detail/form branches below
  // each `return` early — only the list view actually renders the pagination control.
  const { pagedRows: pagedFeedbacks, paginationProps: feedbacksPaginationProps } = usePagination(feedbacks);

  // ── LIST VIEW ─────────────────────────────────────────────────────────────

  if (view === 'list') return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="bold">Interview Feedback</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>New Feedback</Button>
      </Stack>

      {message && <Alert severity="info" onClose={() => setMessage('')} sx={{ mb: 2 }}>{message}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <GridLegacy container spacing={2} alignItems="center">
          <GridLegacy item xs={12} sm={4}>
            <TextField
              size="small" fullWidth label="Search by name / position / interviewer"
              value={search} onChange={e => setSearch(e.target.value)}
            />
          </GridLegacy>
          <GridLegacy item xs={6} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={filterType} label="Type" onChange={e => setFilterType(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="External">External (Hiring)</MenuItem>
                <MenuItem value="Internal">Internal (Employee)</MenuItem>
              </Select>
            </FormControl>
          </GridLegacy>
          <GridLegacy item xs={6} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Decision</InputLabel>
              <Select value={filterDecision} label="Decision" onChange={e => setFilterDecision(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="StrongHire">Strong Hire</MenuItem>
                <MenuItem value="Hire">Hire</MenuItem>
                <MenuItem value="Hold">Hold</MenuItem>
                <MenuItem value="Reject">Reject</MenuItem>
              </Select>
            </FormControl>
          </GridLegacy>
          <GridLegacy item xs={12} sm={2}>
            <Button fullWidth onClick={() => { setSearch(''); setFilterType(''); setFilterDecision(''); }}>
              Clear
            </Button>
          </GridLegacy>
        </GridLegacy>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Candidate / Employee', 'Position', 'Type', 'Date', 'Interviewer', 'Score / 5', 'Trend', 'Decision', 'Actions'].map(h => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {feedbacks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No feedback records found.
                </TableCell>
              </TableRow>
            ) : pagedFeedbacks.map((fb) => {
              // Find the previous interview for the same internal employee (list is newest-first).
              // Looked up by the row's position in the full `feedbacks` array, not its position
              // within the current page, since "previous" only makes sense against the whole list.
              const fullIdx = feedbacks.indexOf(fb);
              const prevForEmployee = fb.interviewType === 'Internal' && fb.employeeId
                ? feedbacks.slice(fullIdx + 1).find(p => p.employeeId === fb.employeeId)
                : null;
              const delta = prevForEmployee && fb.overallScore > 0 && prevForEmployee.overallScore > 0
                ? fb.overallScore - prevForEmployee.overallScore
                : null;

              const scoreColor = fb.overallScore === 0 ? 'text.secondary'
                : fb.overallScore >= 4 ? 'success.main'
                : fb.overallScore >= 3 ? 'warning.main'
                : 'error.main';

              return (
                <TableRow key={fb.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {fb.interviewType === 'Internal' ? <Work fontSize="small" color="primary" /> : <Person fontSize="small" color="action" />}
                      <span>{fb.interviewType === 'Internal' ? (fb.employeeName || fb.candidateName) : fb.candidateName}</span>
                    </Stack>
                  </TableCell>
                  <TableCell>{fb.position}</TableCell>
                  <TableCell>
                    <Chip
                      label={fb.interviewType === 'Internal' ? 'Internal' : 'External'}
                      size="small"
                      color={fb.interviewType === 'Internal' ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{new Date(fb.interviewDate).toLocaleDateString()}</TableCell>
                  <TableCell>{fb.interviewerName}</TableCell>

                  {/* Score column */}
                  <TableCell>
                    {fb.overallScore > 0 ? (
                      <Typography variant="body2" fontWeight={700} color={scoreColor}>
                        {fb.overallScore.toFixed(1)} / 5
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>

                  {/* Trend column — only meaningful for Internal with a previous interview */}
                  <TableCell>
                    {delta !== null ? (
                      <Tooltip title={`Previous: ${prevForEmployee!.overallScore.toFixed(1)} → Now: ${fb.overallScore.toFixed(1)} (${delta > 0 ? '+' : ''}${delta.toFixed(1)})`}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {delta > 0.1
                            ? <TrendingUp fontSize="small" color="success" />
                            : delta < -0.1
                              ? <TrendingDown fontSize="small" color="error" />
                              : <TrendingFlat fontSize="small" color="disabled" />}
                          <Typography variant="caption" color={delta > 0.1 ? 'success.main' : delta < -0.1 ? 'error.main' : 'text.secondary'}>
                            {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    ) : <Typography variant="body2" color="text.secondary">—</Typography>}
                  </TableCell>

                  <TableCell>
                    {fb.finalDecision ? (
                      <Chip label={fb.finalDecision === 'StrongHire' ? 'Strong Hire' : fb.finalDecision}
                        size="small" color={decisionColor(fb.finalDecision) as any} />
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View"><IconButton size="small" onClick={() => openDetail(fb.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(fb.id)}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteConfirm(fb.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {feedbacks.length > 0 && <TablePagination {...feedbacksPaginationProps} />}
      </TableContainer>

      {/* Delete confirmation */}
      <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Feedback?</DialogTitle>
        <DialogContent>This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteConfirm && handleDelete(deleteConfirm)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ── DETAIL VIEW ───────────────────────────────────────────────────────────

  if (view === 'detail' && detailData) return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <IconButton onClick={() => setView('list')}><ArrowBack /></IconButton>
        <Typography variant="h5" fontWeight="bold">Interview Feedback — Detail</Typography>
        <Box flex={1} />
        <Button variant="outlined" startIcon={<Edit />} onClick={() => openEdit(detailData.id)}>Edit</Button>
      </Stack>

      {/* Info header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <GridLegacy container spacing={2}>
          {[
            ['Candidate / Employee', detailData.interviewType === 'Internal' ? (detailData.employeeName || detailData.candidateName) : detailData.candidateName],
            ['Interview Type', detailData.interviewType],
            ['Position', detailData.position],
            ['Interview Date', new Date(detailData.interviewDate).toLocaleDateString()],
            ['Interviewer', detailData.interviewerName],
            ['Experience', detailData.experience || '—'],
            ['Current Company', detailData.currentCompany || '—'],
            ['Notice Period', detailData.noticePeriod || '—'],
            ['Current CTC', detailData.currentCTC || '—'],
            ['Expected CTC', detailData.expectedCTC || '—'],
          ].map(([label, value]) => (
            <GridLegacy item xs={6} sm={4} key={label as string}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="body2" fontWeight={500}>{value}</Typography>
            </GridLegacy>
          ))}
        </GridLegacy>
      </Paper>

      {/* Ratings sections */}
      {[
        { title: 'Technical Evaluation', ratings: detailData.technicalRatings },
        { title: 'Frontend Evaluation', ratings: detailData.frontendRatings },
        { title: 'Communication & Soft Skills', ratings: detailData.softSkillsRatings },
        { title: 'Coding Round Feedback', ratings: detailData.codingRatings },
        { title: 'Overall Assessment', ratings: detailData.overallRatings },
      ].map(section => section.ratings?.length ? (
        <Accordion key={section.title} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={600}>{section.title}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                Avg: {avgRating(section.ratings).toFixed(1)} / 5
              </Typography>
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell>Skill</TableCell><TableCell>Rating</TableCell><TableCell>Comments</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {section.ratings.map((r: SkillRating) => (
                    <TableRow key={r.skill}>
                      <TableCell>{r.skill}</TableCell>
                      <TableCell><Rating value={r.rating} readOnly size="small" /></TableCell>
                      <TableCell>{r.comments || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ) : null)}

      {/* Scenario questions */}
      {detailData.scenarioRatings?.length > 0 && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={600}>Scenario-Based Questions</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {detailData.scenarioRatings.map((s: ScenarioRating, i: number) => (
              <Box key={i} mb={2}>
                <Typography variant="body2" fontWeight={600}>{s.question}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{s.response || '—'}</Typography>
                <Rating value={s.rating} readOnly size="small" />
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Strengths / Areas */}
      <GridLegacy container spacing={2} sx={{ mt: 1 }}>
        <GridLegacy item xs={12} sm={6}>
          <Paper sx={{ p: 2 }}>
            <Typography fontWeight={600} mb={1} color="success.main">Strengths</Typography>
            {detailData.strengths?.filter((s: string) => s).map((s: string, i: number) => (
              <Typography key={i} variant="body2">• {s}</Typography>
            ))}
          </Paper>
        </GridLegacy>
        <GridLegacy item xs={12} sm={6}>
          <Paper sx={{ p: 2 }}>
            <Typography fontWeight={600} mb={1} color="warning.main">Areas of Improvement</Typography>
            {detailData.areasOfImprovement?.filter((s: string) => s).map((s: string, i: number) => (
              <Typography key={i} variant="body2">• {s}</Typography>
            ))}
          </Paper>
        </GridLegacy>
      </GridLegacy>

      {/* Final decision */}
      <Paper sx={{ p: 3, mt: 2 }}>
        <GridLegacy container spacing={2} alignItems="center">
          <GridLegacy item xs={12} sm={3}>
            <Typography variant="caption" color="text.secondary">Overall Score</Typography>
            {detailData.overallScore > 0 ? (
              <Typography variant="h5" fontWeight={700}
                color={detailData.overallScore >= 4 ? 'success.main' : detailData.overallScore >= 3 ? 'warning.main' : 'error.main'}>
                {detailData.overallScore.toFixed(1)}<Typography component="span" variant="body2" color="text.secondary"> / 5</Typography>
              </Typography>
            ) : <Typography variant="body2">No ratings yet</Typography>}
          </GridLegacy>
          <GridLegacy item xs={12} sm={3}>
            <Typography variant="caption" color="text.secondary">Final Decision</Typography>
            {detailData.finalDecision ? (
              <Box><Chip label={detailData.finalDecision === 'StrongHire' ? 'Strong Hire' : detailData.finalDecision}
                color={decisionColor(detailData.finalDecision) as any} sx={{ mt: 0.5 }} /></Box>
            ) : <Typography variant="body2">—</Typography>}
          </GridLegacy>
          <GridLegacy item xs={12} sm={3}>
            <Typography variant="caption" color="text.secondary">Recommended Level</Typography>
            <Typography variant="body2" fontWeight={500}>{detailData.recommendedLevel || '—'}</Typography>
          </GridLegacy>
          <GridLegacy item xs={12} sm={3}>
            <Typography variant="caption" color="text.secondary">Interviewer Remarks</Typography>
            <Typography variant="body2">{detailData.interviewerRemarks || '—'}</Typography>
          </GridLegacy>
        </GridLegacy>
      </Paper>
    </Box>
  );

  // ── FORM VIEW ─────────────────────────────────────────────────────────────

  const selectedEmployee = form.employeeId
    ? employees.find(e => e.id === form.employeeId) || null
    : null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <IconButton onClick={() => setView('list')}><ArrowBack /></IconButton>
        <Typography variant="h5" fontWeight="bold">
          {editingId ? 'Edit Interview Feedback' : 'New Interview Feedback'}
        </Typography>
      </Stack>

      {message && <Alert severity="warning" onClose={() => setMessage('')} sx={{ mb: 2 }}>{message}</Alert>}

      {/* Interview Type Toggle */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Interview Type</Typography>
        <ToggleButtonGroup
          exclusive
          value={form.interviewType}
          onChange={(_, v) => v && setForm(f => ({ ...f, interviewType: v, employeeId: null }))}
          color="primary"
        >
          <ToggleButton value="External">External (Hiring Candidate)</ToggleButton>
          <ToggleButton value="Internal">Internal (Employee Review)</ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Candidate / Employee Info */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>
            {form.interviewType === 'Internal' ? 'Employee & Interview Information' : 'Candidate Information'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <GridLegacy container spacing={2}>
            {form.interviewType === 'Internal' ? (
              <GridLegacy item xs={12} sm={6}>
                <Autocomplete
                  options={employees}
                  getOptionLabel={e => `${e.fullName} (${e.username})`}
                  value={selectedEmployee}
                  onChange={(_, v) => setForm(f => ({ ...f, employeeId: v?.id || null, candidateName: v?.fullName || '' }))}
                  renderInput={params => <TextField {...params} label="Select Employee *" size="small" />}
                />
              </GridLegacy>
            ) : (
              <GridLegacy item xs={12} sm={6}>
                <TextField size="small" fullWidth required label="Candidate Name"
                  value={form.candidateName} onChange={e => setForm(f => ({ ...f, candidateName: e.target.value }))} />
              </GridLegacy>
            )}
            <GridLegacy item xs={12} sm={6}>
              <TextField size="small" fullWidth required label="Position Applied For"
                value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} />
            </GridLegacy>
            <GridLegacy item xs={12} sm={6}>
              <TextField size="small" fullWidth required label="Interview Date" type="date"
                value={form.interviewDate} onChange={e => setForm(f => ({ ...f, interviewDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </GridLegacy>
            <GridLegacy item xs={12} sm={6}>
              <TextField size="small" fullWidth required label="Interviewer Name"
                value={form.interviewerName} onChange={e => setForm(f => ({ ...f, interviewerName: e.target.value }))} />
            </GridLegacy>
            <GridLegacy item xs={12} sm={4}>
              <TextField size="small" fullWidth label="Experience (years)"
                value={form.experience} onChange={e => setForm(f => ({ ...f, experience: e.target.value }))} />
            </GridLegacy>
            <GridLegacy item xs={12} sm={4}>
              <TextField size="small" fullWidth label="Current Company"
                value={form.currentCompany} onChange={e => setForm(f => ({ ...f, currentCompany: e.target.value }))} />
            </GridLegacy>
            <GridLegacy item xs={12} sm={4}>
              <TextField size="small" fullWidth label="Notice Period"
                value={form.noticePeriod} onChange={e => setForm(f => ({ ...f, noticePeriod: e.target.value }))} />
            </GridLegacy>
            <GridLegacy item xs={12} sm={6}>
              <TextField size="small" fullWidth label="Current CTC"
                value={form.currentCTC} onChange={e => setForm(f => ({ ...f, currentCTC: e.target.value }))} />
            </GridLegacy>
            <GridLegacy item xs={12} sm={6}>
              <TextField size="small" fullWidth label="Expected CTC"
                value={form.expectedCTC} onChange={e => setForm(f => ({ ...f, expectedCTC: e.target.value }))} />
            </GridLegacy>
          </GridLegacy>
        </AccordionDetails>
      </Accordion>

      {/* Technical Evaluation */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Technical Evaluation
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Avg: {avgRating(form.technicalRatings).toFixed(1)} / 5
            </Typography>
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell>Skill Area</TableCell><TableCell>Rating (1-5)</TableCell><TableCell>Comments</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {form.technicalRatings.map((r, i) => (
                  <SkillRatingRow key={r.skill} item={r} onChange={u => updateTechnical(i, u)} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Frontend Evaluation */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Frontend Evaluation (If Applicable)
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Avg: {avgRating(form.frontendRatings).toFixed(1)} / 5
            </Typography>
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell>Skill Area</TableCell><TableCell>Rating (1-5)</TableCell><TableCell>Comments</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {form.frontendRatings.map((r, i) => (
                  <SkillRatingRow key={r.skill} item={r} onChange={u => updateFrontend(i, u)} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Scenario-Based Questions */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Scenario-Based Questions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {form.scenarioRatings.map((s, i) => (
            <Box key={s.question} mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600} mb={1}>{s.question}</Typography>
              <GridLegacy container spacing={1}>
                <GridLegacy item xs={12}>
                  <TextField size="small" fullWidth multiline rows={2} label="Candidate Response"
                    value={s.response}
                    onChange={e => updateScenario(i, { ...s, response: e.target.value })}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">Rating:</Typography>
                    <Rating value={s.rating} onChange={(_, v) => updateScenario(i, { ...s, rating: v ?? 0 })} size="small" />
                  </Stack>
                </GridLegacy>
              </GridLegacy>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* Soft Skills */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Communication & Soft Skills
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Avg: {avgRating(form.softSkillsRatings).toFixed(1)} / 5
            </Typography>
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell>Area</TableCell><TableCell>Rating (1-5)</TableCell><TableCell>Comments</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {form.softSkillsRatings.map((r, i) => (
                  <SkillRatingRow key={r.skill} item={r} onChange={u => updateSoftSkills(i, u)} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Coding Round */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Coding Round Feedback
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Avg: {avgRating(form.codingRatings).toFixed(1)} / 5
            </Typography>
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell>Criteria</TableCell><TableCell>Rating (1-5)</TableCell><TableCell>Comments</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {form.codingRatings.map((r, i) => (
                  <SkillRatingRow key={r.skill} item={r} onChange={u => updateCoding(i, u)} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Strengths & Areas */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Strengths & Areas of Improvement</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <GridLegacy container spacing={3}>
            <GridLegacy item xs={12} sm={6}>
              <Typography variant="subtitle2" color="success.main" mb={1}>Strengths</Typography>
              {form.strengths.map((s, i) => (
                <TextField key={i} size="small" fullWidth label={`Strength ${i + 1}`}
                  value={s} sx={{ mb: 1 }}
                  onChange={e => setForm(f => ({ ...f, strengths: f.strengths.map((v, idx) => idx === i ? e.target.value : v) }))}
                />
              ))}
            </GridLegacy>
            <GridLegacy item xs={12} sm={6}>
              <Typography variant="subtitle2" color="warning.main" mb={1}>Areas of Improvement</Typography>
              {form.areasOfImprovement.map((a, i) => (
                <TextField key={i} size="small" fullWidth label={`Area ${i + 1}`}
                  value={a} sx={{ mb: 1 }}
                  onChange={e => setForm(f => ({ ...f, areasOfImprovement: f.areasOfImprovement.map((v, idx) => idx === i ? e.target.value : v) }))}
                />
              ))}
            </GridLegacy>
          </GridLegacy>
        </AccordionDetails>
      </Accordion>

      {/* Overall Assessment */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Overall Assessment
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Avg: {avgRating(form.overallRatings).toFixed(1)} / 5
            </Typography>
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell>Criteria</TableCell><TableCell>Rating (1-5)</TableCell><TableCell>Comments</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {form.overallRatings.map((r, i) => (
                  <SkillRatingRow key={r.skill} item={r} onChange={u => updateOverall(i, u)} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Final Decision */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Final Decision</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <GridLegacy container spacing={2}>
            <GridLegacy item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Final Decision</InputLabel>
                <Select value={form.finalDecision} label="Final Decision"
                  onChange={e => setForm(f => ({ ...f, finalDecision: e.target.value }))}>
                  <MenuItem value=""><em>Not decided</em></MenuItem>
                  <MenuItem value="StrongHire">
                    <Chip size="small" label="Strong Hire" color="success" sx={{ mr: 1 }} /> Strong Hire
                  </MenuItem>
                  <MenuItem value="Hire">
                    <Chip size="small" label="Hire" color="primary" sx={{ mr: 1 }} /> Hire
                  </MenuItem>
                  <MenuItem value="Hold">
                    <Chip size="small" label="Hold" color="warning" sx={{ mr: 1 }} /> Hold
                  </MenuItem>
                  <MenuItem value="Reject">
                    <Chip size="small" label="Reject" color="error" sx={{ mr: 1 }} /> Reject
                  </MenuItem>
                </Select>
              </FormControl>
            </GridLegacy>
            <GridLegacy item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Recommended Level</InputLabel>
                <Select value={form.recommendedLevel} label="Recommended Level"
                  onChange={e => setForm(f => ({ ...f, recommendedLevel: e.target.value }))}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  <MenuItem value="Junior Developer">Junior Developer</MenuItem>
                  <MenuItem value="Mid-Level Developer">Mid-Level Developer</MenuItem>
                  <MenuItem value="Senior Developer">Senior Developer</MenuItem>
                  <MenuItem value="Lead Developer">Lead Developer</MenuItem>
                  <MenuItem value="Architect">Architect</MenuItem>
                </Select>
              </FormControl>
            </GridLegacy>
            <GridLegacy item xs={12}>
              <TextField fullWidth multiline rows={3} label="Interviewer Remarks"
                value={form.interviewerRemarks}
                onChange={e => setForm(f => ({ ...f, interviewerRemarks: e.target.value }))}
              />
            </GridLegacy>
          </GridLegacy>
        </AccordionDetails>
      </Accordion>

      {/* Save bar */}
      <Paper sx={{ p: 2, mt: 3, position: 'sticky', bottom: 0 }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => setView('list')}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingId ? 'Update Feedback' : 'Save Feedback'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default InterviewFeedback;
